import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';

// Zoom floor is the whole diagram filling the frame. Zoom ceiling is about
// two blocks, so a close-up never crops inside a single block.

const OPEN = { minZoom: 0.05, maxZoom: 8 };

function px(value, fallback) {
  if (typeof value === 'number' && value > 0) return value;
  if (typeof value === 'string') {
    const n = parseFloat(value);
    if (n > 0) return n;
  }
  return fallback;
}

function absPos(node, byId) {
  let x = node.position?.x || 0;
  let y = node.position?.y || 0;
  let parent = node.parentId || node.parentNode;
  const seen = new Set();
  while (parent && byId[parent] && !seen.has(parent)) {
    seen.add(parent);
    x += byId[parent].position?.x || 0;
    y += byId[parent].position?.y || 0;
    parent = byId[parent].parentId || byId[parent].parentNode;
  }
  return { x, y };
}

function graphBox(nodes, blockWidth, blockHeight) {
  const byId = Object.fromEntries(nodes.map((n) => [n.id, n]));
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  nodes.forEach((node) => {
    const { x, y } = absPos(node, byId);
    const w = px(node.measured?.width || node.width || node.style?.width, blockWidth);
    const h = px(node.measured?.height || node.height || node.style?.height, blockHeight);
    minX = Math.min(minX, x);
    minY = Math.min(minY, y);
    maxX = Math.max(maxX, x + w);
    maxY = Math.max(maxY, y + h);
  });
  if (!Number.isFinite(minX)) return null;
  return {
    x: minX,
    y: minY,
    width: Math.max(1, maxX - minX),
    height: Math.max(1, maxY - minY),
  };
}

export function useFlowZoom(wrapRef, {
  blockWidth = 220,
  blockHeight = 110,
  padding = 0.02,
  nodeKey = '',
} = {}) {
  const instanceRef = useRef(null);
  const locked = useRef(false);
  const placed = useRef(false);
  const placeRef = useRef(null);
  const sizeRef = useRef('');
  const [limits, setLimits] = useState(OPEN);

  const apply = useCallback((instance) => {
    const el = wrapRef.current;
    if (!instance || !el || typeof instance.getViewport !== 'function') return false;
    const rect = el.getBoundingClientRect();
    if (rect.width < 40 || rect.height < 40) return false;
    const sizeKey = `${Math.round(rect.width)}x${Math.round(rect.height)}`;
    if (sizeRef.current !== sizeKey) {
      sizeRef.current = sizeKey;
      placed.current = false;
      locked.current = false;
    }
    const live = instance.getNodes();
    if (!live.length) return false;
    const box = graphBox(live, blockWidth, blockHeight);
    if (!box) return false;

    // Same formula React Flow uses for fitView: padding is a fraction of the
    // diagram, not of each side. This is the furthest zoom-out allowed.
    // Capped: a one- or two-block graph must not balloon to fill the frame
    // (blocks the size of the canvas, zoom controls pinned). 1.25 keeps a
    // small graph readable at roughly natural size.
    const FIT_CAP = 1.25;
    const floor = Math.min(
      FIT_CAP,
      rect.width / (box.width * (1 + padding)),
      rect.height / (box.height * (1 + padding)),
    );
    if (!(floor > 0.05)) return false;
    const close = Math.min(
      rect.width / (blockWidth * 1.75),
      rect.height / (blockHeight * 1.75),
    );
    const maxZoom = Math.max(floor, close);
    const measured = live.every((n) => n.width && n.height);
    const prev = instance.getViewport();
    // Moving the camera before measurement keeps React Flow from recording
    // block sizes, which leaves the diagram invisible. Clamp only.
    if (measured) {
      const atFloor = Math.abs(prev.zoom - floor) <= 0.02;
      // A zoom above the floor only counts once the fitted view has landed.
      // Otherwise the default zoom of 1 is mistaken for the user zooming in.
      const zoomedIn = locked.current && prev.zoom > floor + 0.03;
      if (atFloor) locked.current = true;
      const zoom = zoomedIn ? Math.min(prev.zoom, maxZoom) : floor;
      const cx = box.x + box.width / 2;
      const cy = box.y + box.height / 2;
      const next = zoomedIn
        ? { x: prev.x, y: prev.y, zoom }
        : {
          x: rect.width / 2 - cx * zoom,
          y: rect.height / 2 - cy * zoom,
          zoom,
        };
      const moved = Math.abs(next.zoom - prev.zoom) > 0.005
        || Math.abs(next.x - prev.x) > 1
        || Math.abs(next.y - prev.y) > 1;
      // One placement. The bridge writes it synchronously; setViewport's
      // animation gets cancelled if we call it again before it lands.
      if (moved && !placed.current && placeRef.current) {
        placed.current = !!placeRef.current(next);
      }
    } else if (prev.zoom < floor - 0.005) {
      instance.zoomTo(floor, { duration: 0 });
    }
    // Users may always zoom out to half the fitted view (room to see context
    // and to drop blocks beside a small graph); the fit itself stays the floor.
    const minZoom = Math.min(floor, Math.max(0.25, floor * 0.5));
    setLimits((cur) => (
      Math.abs(cur.minZoom - minZoom) < 0.005 && Math.abs(cur.maxZoom - maxZoom) < 0.005
        ? cur
        : { minZoom, maxZoom }
    ));
    // Stay on it until the fitted zoom is actually on screen. setViewport
    // applies on the next frame, and a second pass was keeping the default.
    return measured && Math.abs(instance.getZoom() - floor) <= 0.02;
  }, [wrapRef, blockWidth, blockHeight, padding]);

  const onInit = useCallback((instance) => {
    instanceRef.current = instance;
    apply(instance);
  }, [apply]);

  useEffect(() => {
    let raf = 0;
    let timer = 0;
    let stopped = false;
    let tries = 0;
    const tick = () => {
      if (stopped) return;
      const instance = instanceRef.current;
      if (instance && apply(instance)) return;
      tries += 1;
      if (tries > 40) return;
      raf = requestAnimationFrame(() => {
        timer = window.setTimeout(tick, 50);
      });
    };
    tick();
    return () => {
      stopped = true;
      cancelAnimationFrame(raf);
      clearTimeout(timer);
    };
  }, [nodeKey, apply]);

  useEffect(() => {
    const el = wrapRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    let last = '';
    const ro = new ResizeObserver(() => {
      const r = el.getBoundingClientRect();
      const key = `${Math.round(r.width)}x${Math.round(r.height)}`;
      if (key === last) return;
      last = key;
      apply(instanceRef.current);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [wrapRef, apply]);

  return { minZoom: limits.minZoom, maxZoom: limits.maxZoom, onInit, placeRef };
}

// Render inside ReactFlow. Places the camera synchronously so the fitted
// view is on screen without waiting for a zoom animation.
export function FlowZoomBridge({ useStoreApi, placeRef }) {
  const store = useStoreApi();
  useLayoutEffect(() => {
    placeRef.current = (next) => {
      const { d3Zoom, d3Selection } = store.getState();
      const node = d3Selection?.node?.();
      const current = node?.__zoom;
      if (!d3Zoom || !d3Selection || !current?.constructor) return false;
      d3Zoom.transform(d3Selection, new current.constructor(next.zoom, next.x, next.y));
      return true;
    };
    return () => {
      placeRef.current = null;
    };
  }, [store, placeRef]);
  return null;
}
